import React, { useState, useEffect } from 'react';
import { FaCalendar, FaTimes } from 'react-icons/fa';
import { collection, addDoc, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../../config/firebaseConfig';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './AddBudgetModal.module.css';
import { useTheme } from '../../../contexts/ThemeContext';

const AddBudgetModal = ({ isOpen, onClose, onSubmit, projectId }) => {
  const { isDarkMode } = useTheme();
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    BütçeAdı: '',
    BütçeKaynak: '',
    BütçeSahibi: '',
    BütçeTipi: '',
    BütçeKategorisi: '',
    GiderTipi: '',
    ParaBirimi: '',
    KDV: '',
    BütçeMiktarı: '',
    BütçeTarihi: '',
    BeklenenÖdemeTarihi: '',
    ÖdemeDurumu: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Add document to Firestore with projectId
      const docRef = await addDoc(collection(db, 'Budgets'), {
        ...formData,
        projectId: projectId, // Add the project ID to the budget data
        createdAt: new Date().toISOString()
      });

      toast.success('Bütçe başarıyla eklendi!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Reset form and close modal
      handleReset();
      onSubmit(formData);
      onClose();

    } catch (error) {
      console.error('Error adding budget:', error);
      toast.error('Bütçe eklenirken bir hata oluştu!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      BütçeAdı: '',
      BütçeKaynak: '',
      BütçeSahibi: '',
      BütçeTipi: '',
      BütçeKategorisi: '',
      GiderTipi: '',
      ParaBirimi: '',
      KDV: '',
      BütçeMiktarı: '',
      BütçeTarihi: '',
      BeklenenÖdemeTarihi: '',
      ÖdemeDurumu: ''
    });
  };

  // Fetch employees when component mounts
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const employeesQuery = query(collection(db, 'Employees'), orderBy('eName'));
        const querySnapshot = await getDocs(employeesQuery);
        const employeesList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setEmployees(employeesList);
      } catch (error) {
        console.error('Error fetching employees:', error);
        toast.error('Çalışanlar yüklenirken bir hata oluştu!');
      }
    };

    if (isOpen) {
      fetchEmployees();
    }
  }, [isOpen]);

  // ESC key to close modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
    }
    
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Bütçe Ekle</h2>
          <button 
            type="button" 
            className={styles.closeButton} 
            onClick={onClose}
            aria-label="Close modal"
          >
            <FaTimes />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          <form onSubmit={handleSubmit}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Bütçe Adı</label>
                <input
                  type="text"
                  name="BütçeAdı"
                  value={formData.BütçeAdı}
                  onChange={handleChange}
                  className={styles.input}
                  required
                  placeholder="Bütçe adını giriniz"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Bütçe Kaynak</label>
                <select
                  name="BütçeKaynak"
                  value={formData.BütçeKaynak}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="">Lütfen Seçiniz</option>
                  <option value="kaynak1">Kaynak 1</option>
                  <option value="kaynak2">Kaynak 2</option>
                  <option value="kaynak3">Kaynak 3</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Bütçe Sahibi</label>
                <select
                  name="BütçeSahibi"
                  value={formData.BütçeSahibi}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="">Lütfen Seçiniz</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.eName} {employee.eSurname}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Bütçe Tipi</label>
                <select
                  name="BütçeTipi"
                  value={formData.BütçeTipi}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="">Lütfen Seçiniz</option>
                  <option value="tip1">Tip 1</option>
                  <option value="tip2">Tip 2</option>
                  <option value="tip3">Tip 3</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Bütçe Kategorisi</label>
                <select
                  name="BütçeKategorisi"
                  value={formData.BütçeKategorisi}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="">Lütfen Seçiniz</option>
                  <option value="kategori1">Kategori 1</option>
                  <option value="kategori2">Kategori 2</option>
                  <option value="kategori3">Kategori 3</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Gider Tipi</label>
                <select
                  name="GiderTipi"
                  value={formData.GiderTipi}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="">Lütfen Seçiniz</option>
                  <option value="kira">Kira</option>
                  <option value="elektrik">Elektrik</option>
                  <option value="diğer giderler">Diğer Giderler</option>
                </select>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Para Birimi</label>
                <select
                  name="ParaBirimi"
                  value={formData.ParaBirimi}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="">Lütfen Seçiniz</option>
                  <option value="TRY">TRY</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Bütçe Miktarı</label>
                <input
                  type="number"
                  name="BütçeMiktarı"
                  value={formData.BütçeMiktarı}
                  onChange={handleChange}
                  className={styles.input}
                  placeholder="0.00"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>KDV(%)</label>
                <input
                  type="number"
                  name="KDV"
                  value={formData.KDV}
                  onChange={handleChange}
                  className={styles.input}
                  placeholder="0"
                  required
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Bütçe Tarihi</label>
                <div className={styles.dateInputWrapper}>
                  <input
                    type="date"
                    name="BütçeTarihi"
                    value={formData.BütçeTarihi}
                    onChange={handleChange}
                    className={styles.input}
                    required
                  />
                  <FaCalendar className={styles.calendarIcon} />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Beklenen Ödeme Tarihi</label>
                <div className={styles.dateInputWrapper}>
                  <input
                    type="date"
                    name="BeklenenÖdemeTarihi"
                    value={formData.BeklenenÖdemeTarihi}
                    onChange={handleChange}
                    className={styles.input}
                    required
                  />
                  <FaCalendar className={styles.calendarIcon} />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Ödeme Durumu</label>
                <select
                  name="ÖdemeDurumu"
                  value={formData.ÖdemeDurumu}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="">Lütfen Seçiniz</option>
                  <option value="Ödendi">Ödendi</option>
                  <option value="Beklemede">Beklemede</option>
                  <option value="İptal">İptal</option>
                </select>
              </div>
            </div>

            <div className={styles.buttonGroup}>
              <button
                type="button"
                onClick={onClose}
                className={`${styles.button} ${styles.cancelButton}`}
                disabled={isSubmitting}
              >
                Kapat
              </button>
              <button
                type="button"
                onClick={handleReset}
                className={`${styles.button} ${styles.resetButton}`}
                disabled={isSubmitting}
              >
                Temizle
              </button>
              <button
                type="submit"
                className={`${styles.button} ${styles.submitButton}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Ekleniyor...' : 'Ekle'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddBudgetModal;
